import { getProp } from "./any.tools";
import { anyReallyAny } from "./object.tools";

export interface CommonEventInit {
  bubbles?: boolean;
  cancelable?: boolean;
  composed?: boolean;
}

export const FullEventInit: CommonEventInit = {
  bubbles: true,
  cancelable: true,
  composed: true
};

export const NoBubblesEventInit: CommonEventInit = {
  bubbles: false,
  cancelable: true,
  composed: true
};

const getEventsKey = "getEvents";
const internalEventsList = "eventHandlers__list";

/** A factory of property decorator that installs a CommonEventHandler on the event property */
export function eventFactory<EventData>(eventHandler: (eventKey: string, el: unknown) => CommonEventHandler<EventData>) {
  return (classPrototype: anyReallyAny, eventKey: string): void => {
    const internalProperty = "eventHandler__" + eventKey;

    // Fill internal list of events
    if (classPrototype[internalEventsList]) {
      classPrototype[internalEventsList] = [...classPrototype[internalEventsList], eventKey];
    } else {
      classPrototype[internalEventsList] = [eventKey];
    }

    Object.defineProperty(classPrototype, eventKey, {
      get: function () {
        let handler = this[internalProperty];
        if (!handler) {
          this[internalProperty] = handler = eventHandler(eventKey, this);
        }
        return handler;
      }
    });

    // Define a "getEvents" function if not already added
    if (!classPrototype[getEventsKey]) {
      Object.defineProperty(classPrototype, getEventsKey, {
        configurable: true,
        value: function () {
          return this ? getProp(this, internalEventsList) : [];
        }
      });
    }

  };
}

/**
 * Property decorator that installs a CommonEventHandler on the event property
 */
export function CommonEvent<EventInitOptions extends CommonEventInit = CommonEventInit>(defaultOptions?: EventInitOptions): (classPrototype: anyReallyAny, eventKey: string) => void {
  return eventFactory(eventKey => new CommonListEventHandler(eventKey, defaultOptions));
}

/**
 * The event handler handles add/remove listeners and emit event
 */
export interface CommonEventHandler<EventData = void, EventInitOptions extends CommonEventInit = CommonEventInit> {

  /**
   * event name
   */
  event: string;

  /**
   * This can help determine if there is a listener attached to this event
   * onWasCalled is set to true if "on" function was called
   * * this does not detect calls to addEventListeners for this event
   */
  onWasCalled: boolean;

  /**
   * register listener on the event
   * @returns this event handler
   */
  on(listener: CommonListenerOrEventListenerObject<EventData>, options?: boolean | CommonAddEventListenerOptions): CommonEventHandler<EventData>;

  /**
   * register listener on the event
   * @returns this event handler
   */
  off(listener: CommonListenerOrEventListenerObject<EventData>): CommonEventHandler<EventData>;

  /**
   * emit CustomEvent with eventData
   */
  emit(eventData: EventData, options?: EventInitOptions): void;
}

export interface CommonEvent<T> {
  detail: T;
}

export type CommonListener<EventData> = (event: CommonEvent<EventData>) => void;
export interface CommonAddEventListenerOptions {
  once?: boolean;
  passive?: boolean;
  capture?: boolean;
}
export interface CommonListenerObject<EventData> {
  /**
   * A function that is called whenever an event of the specified type occurs.
   */
  handleEvent(event: CommonEvent<EventData>): void;
}

export type CommonListenerOrEventListenerObject<EventData> = CommonListener<EventData> | CommonListenerObject<EventData>;

/** Test if listener has handleEvent to safely cast to CommonListenerObject */
export function isCommonListenerObject<EventData>(listener: CommonListenerOrEventListenerObject<EventData>): listener is CommonListenerObject<EventData> {
  return (listener as CommonListenerObject<EventData>).handleEvent !== undefined;
}

type Listener<EventData> = {
  callback: CommonListenerOrEventListenerObject<EventData>,
  options?: boolean | CommonAddEventListenerOptions,
  wasCalled?: boolean
};
/**
 * CommonEventHandler implementation that holds a listeners list
 */
export class CommonListEventHandler<EventData, EventInitOptions extends CommonEventInit = CommonEventInit> implements CommonEventHandler<EventData, EventInitOptions> {

  protected _listeners: Map<CommonListenerOrEventListenerObject<EventData>, Listener<EventData>> | undefined;

  constructor(public event: string, protected _defaultOptions?: EventInitOptions) {

  }

  public onWasCalled = false;

  /**
   * register listener on the event
   * @returns this event handler
   */
  public on(listener: CommonListenerOrEventListenerObject<EventData>, options?: boolean | CommonAddEventListenerOptions): CommonEventHandler<EventData> {
    this.onWasCalled = true;
    this._listeners ??= new Map();
    this._listeners.set(listener, { callback: listener, options });
    return this;
  }

  /**
   * register listener on the event
   * @returns this event handler
   */
  public off(listener: CommonListenerOrEventListenerObject<EventData>): CommonEventHandler<EventData> {
    if (this._listeners) {
      this._listeners.delete(listener);
    }
    return this;
  }

  /**
   * emit CustomEvent with eventData
   */
  public emit(eventData: EventData, options?: EventInitOptions): void {
    if (this._listeners) {
      for (const l of Array.from(this._listeners.values())) {
        if (isCommonListenerObject(l.callback)) {
          l.callback.handleEvent({ detail: eventData });
        } else if (l.callback) {
          l.callback({ detail: eventData });
        }
        l.wasCalled = true;
        if ((l.options as CommonAddEventListenerOptions)?.once) {
          // remove listeners that were registered with "once" option and wasCalled
          this._listeners.delete(l.callback);// deleting during interation on values is ok
        }
      }
    }
  }
}